import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ComponentPropsWithoutRef,
  type ReactNode
} from 'react'
import { LoaderCircle } from 'lucide-react'
import { Slot } from 'radix-ui'
import {
  DisabledHintTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '#components/Tooltip'
import { cn } from '#lib/utils'

export type IconButtonVariant =
  'ghost' | 'tint' | 'toggle' | 'floating' | 'strong' | 'destructive' | 'create' | 'success'
export type IconButtonSize = 'compact' | 'xs' | 'sm' | 'md'

// 中性语气的浮层打开状态也显示选中；描边开关只跟随实际选择。
const NEUTRAL_STATES =
  'hover:bg-button-ghost-hover hover:text-foreground focus-visible:bg-button-ghost-hover focus-visible:text-foreground active:bg-button-neutral-hover aria-pressed:bg-button-selected aria-pressed:text-foreground data-[state=open]:bg-button-selected data-[state=open]:text-foreground'
const variantClass: Record<IconButtonVariant, string> = {
  ghost: `text-muted-foreground ${NEUTRAL_STATES}`,
  tint: `bg-button-neutral text-foreground ${NEUTRAL_STATES} hover:bg-button-neutral-hover focus-visible:bg-button-neutral-hover active:bg-button-neutral-pressed`,
  toggle: 'border border-control-border text-foreground hover:bg-accent focus-visible:bg-accent active:bg-accent',
  floating: `bg-popover text-foreground shadow-md ${NEUTRAL_STATES} hover:bg-button-floating-hover focus-visible:bg-button-floating-hover active:bg-button-floating-selected aria-pressed:bg-button-floating-selected data-[state=open]:bg-button-floating-selected`,
  strong:
    'bg-button-neutral-strong text-button-neutral-strong-foreground hover:bg-button-neutral-strong-hover focus-visible:bg-button-neutral-strong-hover active:bg-button-neutral-strong-pressed',
  destructive:
    'text-muted-foreground hover:bg-button-danger hover:text-destructive focus-visible:bg-button-danger focus-visible:text-destructive active:bg-button-danger-hover',
  create:
    'text-button-create-foreground hover:bg-button-create focus-visible:bg-button-create active:bg-button-create-hover aria-pressed:bg-button-create data-[state=open]:bg-button-create',
  success: 'text-success hover:bg-success/10 focus-visible:bg-success/10'
}

// 选中后保留同一底色，鼠标、键盘和按下状态不能被未选中的交互样式盖过。
const SELECTED_NEUTRAL_CLASS =
  'bg-button-selected text-foreground hover:bg-button-selected focus-visible:bg-button-selected active:bg-button-selected'
const selectedClass: Record<IconButtonVariant, string> = {
  ghost: SELECTED_NEUTRAL_CLASS,
  tint: SELECTED_NEUTRAL_CLASS,
  toggle: 'border-ai/60 bg-ai/10 text-ai hover:bg-ai/10 focus-visible:bg-ai/10 active:bg-ai/10',
  floating: 'bg-button-floating-selected text-foreground hover:bg-button-floating-selected focus-visible:bg-button-floating-selected active:bg-button-floating-selected',
  strong: '',
  destructive: '',
  create: 'bg-button-create',
  success: ''
}

// 图标大小由按钮尺寸决定；图标自带 size-* 类时以图标为准。
const sizeClass: Record<IconButtonSize, string> = {
  compact: "size-5 [&_svg:not([class*='size-'])]:size-3",
  xs: "size-7 [&_svg:not([class*='size-'])]:size-3.5",
  sm: "size-8 [&_svg:not([class*='size-'])]:size-4",
  md: "size-9 [&_svg:not([class*='size-'])]:size-4"
}

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'title' | 'aria-label'
> {
  /** 按钮的名字：读屏朗读与悬停提示共用。 */
  label: string
  /** 悬停提示需要与名字不同的内容时传入；默认显示名字。false 不显示提示（如目录的展开箭头）。 */
  tooltip?: ReactNode | false
  tooltipSide?: ComponentPropsWithoutRef<typeof TooltipContent>['side']
  /** 显示选中外观但不是开关；开关用 aria-pressed。 */
  selected?: boolean
  /** 圆形：列表行里的试听键、播放条的播放键和浮动的回到底部。 */
  shape?: 'square' | 'round'
  /** 外观套到唯一子元素上，如打开外部页面的图标链接。 */
  asChild?: boolean
  variant?: IconButtonVariant
  size?: IconButtonSize
  /** 处理中：图标换成转圈，同时禁用按钮。 */
  loading?: boolean
}

// 提示只由自绘 Tooltip 承担；原生 title 会在悬停时再叠出一个系统提示。
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    tooltip,
    tooltipSide,
    selected = false,
    shape = 'square',
    asChild = false,
    variant = 'ghost',
    size = 'sm',
    className,
    type = 'button',
    loading = false,
    disabled: disabledProp,
    children,
    ...props
  },
  ref
) {
  const Comp = asChild ? Slot.Root : 'button'
  const disabled = disabledProp || loading
  const highlighted = selected || (
    variant === 'toggle' && (props['aria-pressed'] === true || props['aria-pressed'] === 'true')
  )
  const button = (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      aria-label={label}
      // 空 title 挡住外层行的原生 title，悬停时只出现这一个提示。
      title=""
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:shrink-0',
        shape === 'round' ? 'rounded-full' : 'rounded-md',
        variantClass[variant],
        sizeClass[size],
        highlighted && selectedClass[variant],
        className
      )}
      disabled={disabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && !asChild ? (
        <LoaderCircle aria-hidden="true" className="animate-spin" />
      ) : (
        children
      )}
    </Comp>
  )
  if (tooltip === false) return button
  const hint = tooltip ?? label

  // 另给了说明（如为什么不能用）时，禁用按钮由外层触发区承接悬停与聚焦；
  // 只有名字时保持原生禁用，不进入 Tab 顺序。
  const trigger =
    disabled && tooltip !== undefined ? (
      <DisabledHintTrigger label={label} tabIndex={props.tabIndex ?? 0}>
        {button}
      </DisabledHintTrigger>
    ) : (
      button
    )

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side={tooltipSide}>{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
})
